import { useCallback, useEffect, useRef, useState } from "react";
import { Captions, Pause, Play, Volume2, VolumeX, X } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { tierLabel, walkthroughFor } from "@/lib/learn";
import ElmMark from "@/components/ElmMark";

const readMs = (t) => Math.max(5200, Math.round((t.split(/\s+/).length / 2.6) * 1000) + 1400);

export default function Walkthrough({ onClose }) {
  const { user, refreshUser } = useAuth();
  const steps = walkthroughFor(user.role);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [showCaptions, setShowCaptions] = useState(true);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const urlCache = useRef({});
  const step = steps[i];
  const last = steps.length - 1;

  const clearTimer = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };

  const finish = useCallback(() => {
    clearTimer();
    if (audioRef.current) audioRef.current.pause();
    api.post("/auth/tour-seen").then(() => refreshUser()).catch(() => {});
    onClose();
  }, [onClose, refreshUser]);

  const advance = useCallback(() => { setI((n) => (n >= last ? n : n + 1)); }, [last]);

  // Drive narration / auto-advance when the step changes or play state toggles.
  useEffect(() => {
    clearTimer();
    const audio = audioRef.current;
    if (audio) audio.pause();
    if (!playing) return;
    let cancelled = false;

    const fallback = () => { timerRef.current = setTimeout(() => !cancelled && advance(), readMs(step.narration)); };

    if (muted) { fallback(); return () => { cancelled = true; clearTimer(); }; }

    const play = async () => {
      try {
        let url = urlCache.current[i];
        if (!url) {
          const { data } = await api.post("/learn/narration", { text: step.narration });
          url = fileUrl(data.url);
          urlCache.current[i] = url;
        }
        if (cancelled || !audioRef.current) return;
        audioRef.current.src = url;
        audioRef.current.onended = () => !cancelled && advance();
        await audioRef.current.play();
      } catch {
        if (!cancelled) fallback();
      }
    };
    play();
    return () => { cancelled = true; clearTimer(); };
  }, [i, playing, muted, step, advance]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight") { if (i < last) advance(); else finish(); }
      if (e.key === "ArrowLeft") setI((n) => Math.max(0, n - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [i, last, advance, finish]);

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-stone-950/95 backdrop-blur-sm" data-testid="walkthrough-overlay">
      <audio ref={audioRef} className="hidden" />
      <div className="flex items-center justify-between px-5 py-4 text-white/80 sm:px-8">
        <ElmMark light />
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="sm" className="text-white/80 hover:bg-white/10 hover:text-white" onClick={() => setShowCaptions((v) => !v)} data-testid="walkthrough-captions-toggle" aria-label="Toggle captions">
            <Captions className={`h-4 w-4 ${showCaptions ? "" : "opacity-40"}`} />
          </Button>
          <Button variant="ghost" size="sm" className="text-white/80 hover:bg-white/10 hover:text-white" onClick={() => setMuted((v) => !v)} data-testid="walkthrough-mute-toggle" aria-label="Toggle sound">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="sm" className="text-white/80 hover:bg-white/10 hover:text-white" onClick={finish} data-testid="walkthrough-skip"><X className="mr-1.5 h-4 w-4" />Skip</Button>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center px-5 sm:px-8">
        <div key={step.k} className="rise mx-auto w-full max-w-2xl text-center text-white" data-testid={`walkthrough-step-${step.k}`}>
          <p className="eyebrow !text-white/50">Step {i + 1} of {steps.length} · {tierLabel(step.tier)}</p>
          <h2 className="mt-4 font-serif text-3xl leading-tight sm:text-5xl">{step.title}</h2>
          {showCaptions && <p className="mx-auto mt-6 max-w-xl text-base text-white/75 sm:text-lg" data-testid="walkthrough-caption">{step.caption}</p>}
        </div>
      </div>

      <div className="mx-auto w-full max-w-2xl px-5 pb-8 sm:px-8">
        <Progress value={((i + 1) / steps.length) * 100} className="h-1 bg-white/15" data-testid="walkthrough-progress" />
        <div className="mt-4 flex items-center justify-between">
          <Button variant="ghost" className="text-white/70 hover:bg-white/10 hover:text-white disabled:opacity-30" disabled={i === 0} onClick={() => setI((n) => Math.max(0, n - 1))} data-testid="walkthrough-prev">Back</Button>
          <Button variant="ghost" size="icon" className="h-11 w-11 rounded-full border border-white/20 text-white hover:bg-white/10" onClick={() => setPlaying((v) => !v)} data-testid="walkthrough-playpause" aria-label={playing ? "Pause" : "Play"}>
            {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
          </Button>
          {i < last
            ? <Button className="rounded-full bg-white text-stone-900 hover:bg-white/90" onClick={advance} data-testid="walkthrough-next">Next</Button>
            : <Button className="rounded-full bg-white text-stone-900 hover:bg-white/90" onClick={finish} data-testid="walkthrough-done">Done</Button>}
        </div>
      </div>
    </div>
  );
}
